// src/components/ServiceLevelAgreement.js

import React from 'react';
import enirisLogo from '../img/logo.png';

const ServiceLevelAgreement = () => {
  return (
    <div className="bg-gray-100 min-h-screen p-8">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-4xl mx-auto">
        <div className="flex justify-center mb-6">
          <img
            src={enirisLogo}
            alt="Eniris Logo"
            className="h-12"
          />
        </div>

        {/* Content Section */}
        <div className="text-gray-800 space-y-6">
          <h1 className="text-3xl font-bold mb-4">Annex - Service Level Agreement</h1>
          <p className="text-sm italic">
            It is important to note that this Service Level Agreement applies only after internal agreement and the associated premium has been paid upfront.
          </p>
          <p>
            This Service Level Agreement ("SLA") is an annex to and forms part of the Agreement by and between Eniris and the Client, in connection with the products and services provided by Eniris to the Client.
          </p>

          {/* Section 1: General */}
          <h2 className="text-2xl font-semibold mt-8">1. General</h2>
          <p>
            <span className="font-semibold">1.1.</span> The purpose of this SLA is to set out the service levels that shall apply to the maintenance and support services provided by Eniris.
          </p>
          <p>
            <span className="font-semibold">1.2.</span> This SLA shall be subject to and governed by the provisions of the Agreement. In the event of conflict between the provisions of this SLA and the Agreement, the provisions of the Agreement shall prevail except when expressly otherwise provided for in this SLA.
          </p>

          {/* Section 2: Definitions */}
          <h2 className="text-2xl font-semibold mt-8">2. Definitions and Interpretation</h2>
          <p>
            <span className="font-semibold">2.1.</span> Capitalized terms used throughout this SLA shall have the meaning ascribed to them below:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><span className="font-semibold">"Business Day"</span> means Monday to Friday from 10:00 to 16:00 (CET) excluding Belgian public holidays.</li>
            <li><span className="font-semibold">"Documentation"</span> available through our documentation portal: docs.eniris.io.</li>
            <li><span className="font-semibold">"Helpdesk"</span> means the support helpdesk available under https://eniris.io/ (as may be changed by Eniris from time to time and notified to the Client).</li>
            <li><span className="font-semibold">"Incident"</span> means any event which is not part of the standard operation of a key functionality during normal use of the Platform and which causes, or may cause, an interruption to or a reduction in the quality of the Platform resulting in a failure to comply with the specifications of the Platform.</li>
            <li><span className="font-semibold">"Agreement"</span> means the Order Form and Terms and Conditions.</li>
            <li><span className="font-semibold">"Platform"</span> means the Eniris Insights / SmartgridOne / SmartgridX / Eniris EMS / Eniris BMS.</li>
            <li><span className="font-semibold">"Service Request"</span> means a request in relation to configuration and functional issues, which may include, but is not limited to, a request to give general advice or information concerning the use of the Platform.</li>
            <li><span className="font-semibold">"Support Contact"</span> means one or more of the Client's end users appointed by the Client (and approved by Eniris) for support related matters.</li>
            <li><span className="font-semibold">"Target Resolution Time"</span> means the period (i) starting from the moment when Eniris has sent a notice that the Incident report has been received, contains all the required information and the information contained herein has been understood.</li>
            <li><span className="font-semibold">"Target Response Time"</span> means the time between the Incident being reported and a notice from Eniris that the Incident report has been received, contains all the required information and the information contained herein has been understood.</li>
            <li><span className="font-semibold">"Update"</span> means updates, enhancements, modifications or additional features that, in Eniris sole discretion, are deemed to be minor logical improvements or extensions.</li>
          </ul>
          <p className="mt-4">
            <span className="font-semibold">2.2.</span> Terms not defined in this clause 2 shall have the meaning given to them in the Agreement, unless the context requires otherwise.
          </p>

          {/* Section 3: Support Services */}
          <h2 className="text-2xl font-semibold mt-8">3. Support Services</h2>
          <p>
            <span className="font-semibold">3.1.</span> Subject to the terms and conditions of this SLA and timely payment of the relevant fees, Eniris shall:
          </p>
          <p className="ml-4">
            a) use commercially reasonable efforts to provide remote support via the Helpdesk on Business Days to:
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;(1) respond to Service Requests that cannot be addressed by the Client after consultation of Documentation; and
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;(ii) provide the Client with resolutions and/or workarounds for Incidents.
          </p>
          <p className="ml-4">
            b) make Updates available to the Client on a periodic basis and install such Updates when Eniris deems appropriate.
          </p>
          {/* Add rest of section 3 text here... */}
          <p>
            <span className="font-semibold">3.2.</span> The Client acknowledges that Eniris may, at its sole discretion, consider any of the following out of scope of this SLA (hereafter referred to "Out of Scope Incidents" or "Out of Scope Service Request"):
          </p>
          <ul className="list-disc list-inside ml-8 space-y-1">
            <li>Incidents or Service Requests related to malfunctioning of third party interfaces with third party software or hardware;</li>
            <li>Incidents or Service Requests cause by a force majeure event;</li>
            <li>Incidents or Service Requests related to the breach by the Client of the Agreement;</li>
            <li>Incidents or Service Requests related to modifications or additions to the Platform not performed by or with the consent of Eniris;</li>
            <li>Incidents or Service Requests induced by the Client due to incorrect use of the Platform;</li>
            <li>Incidents or Service Requests by non-supported data formats;</li>
            <li>Incidents or Service Requests caused by third-party hardware or software;</li>
            <li>Incidents or Service Requests caused by the Client's failure to maintain an adequate and stable internet connection required to operate the Platform; or</li>
            <li>any other Incidents or Service Requests not caused by or related to the Platform.</li>
          </ul>
          
          {/* Section 4: Incident Reporting */}
          <h2 className="text-2xl font-semibold mt-8">4. Incident Reporting</h2>
          <p>
            <span className="font-semibold">4.1.</span> If the Client encounters an Incident, the Client shall first review and assess the Incident.
          </p>
          <p>
            <span className="font-semibold">4.2.</span> In its notification, the Support Contact must include a clear description of the Incident or Service Request and, in case of an Incident, provide all necessary information in order to allow Eniris to reproduce the reported Incident.
          </p>
          <p>
            <span className="font-semibold">4.3.</span> If an Incident is reported repeatedly without providing new technical information or without any change in the underlying conditions, Eniris reserves the right not to process such reports within the SLA timelines.
          </p>

          {/* Section 5: Service Levels */}
          <h2 className="text-2xl font-semibold mt-8">5. Service Levels</h2>
          <p>
            <span className="font-semibold">5.1.</span> Based on the information provided by the Client, Eniris shall at its sole discretion assign a priority level to each reported Incident. Priority levels are assigned according to the following methodology:
          </p>
          <div className="overflow-x-auto my-4">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b border-gray-300 bg-gray-200">Severity</th>
                  <th className="py-2 px-4 border-b border-gray-300 bg-gray-200">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-2 px-4 font-semibold">Severity 1 (blocking)</td>
                  <td className="py-2 px-4">The Incident causes a failure of the entire Platform or of a major component thereof, preventing the use of the entire Platform or resulting in a major business disruption which affects multiple end-users unable to complete their time critical activities as a result of the Incident. No workaround is available.</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-2 px-4 font-semibold">Severity 2 (urgent)</td>
                  <td className="py-2 px-4">The Incident causes a severe service degradation of the Platform. End-users are not able to complete their time critical activities as a result of the Incident, but a workaround is either readily available to the end-user or was previously provided by Eniris.</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-2 px-4 font-semibold">Severity 3 (basic)</td>
                  <td className="py-2 px-4">Individual functionality is not performing properly but without an immediate impact on time critical activities.</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-2 px-4 font-semibold">Severity 4 (low)</td>
                  <td className="py-2 px-4">Minimal or no impact on the end-user (e.g. cosmetic user interface issue).</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            <span className="font-semibold">5.2.</span> Eniris shall (i) respond to Incidents notified to Eniris with the Target Response Times set out in the table below, and (ii) aim to provide a resolution or workaround within the Target Resolution Times set out in the table below.
          </p>
          <div className="overflow-x-auto my-4">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b border-gray-300 bg-gray-200">Severity</th>
                  <th className="py-2 px-4 border-b border-gray-300 bg-gray-200">Target Response Time</th>
                  <th className="py-2 px-4 border-b border-gray-300 bg-gray-200">Target Resolution Time</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-2 px-4 font-semibold">Severity 1 (blocking)</td>
                  <td className="py-2 px-4">2 working days</td>
                  <td className="py-2 px-4">5 working days</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-2 px-4 font-semibold">Severity 2 (urgent)</td>
                  <td className="py-2 px-4">5 working days</td>
                  <td className="py-2 px-4">10 working days</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-2 px-4 font-semibold">Severity 3 (basic)</td>
                  <td className="py-2 px-4">10 working days</td>
                  <td className="py-2 px-4">3 working weeks</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-2 px-4 font-semibold">Severity 4 (low)</td>
                  <td className="py-2 px-4">3 working weeks</td>
                  <td className="py-2 px-4">(best effort)</td>
                </tr>
              </tbody>
            </table>
          </div>
        
          {/* Section 6: Availability */}
          <h2 className="text-2xl font-semibold mt-8">6. Availability</h2>
          <p>
            <span className="font-semibold">6.1.</span> Eniris shall use its best efforts to ensure a yearly availability of the Platform of 99,5% during Business Day.
          </p>

          {/* Section 7: Privacy and Data Protection */}
          <h2 className="text-2xl font-semibold mt-8">7. Privacy and Data Protection</h2>
          <p>
            <span className="font-semibold">7.1.</span> If and to the extent Eniris processes personal data on behalf of the Client in connection with this SLA, Eniris shall process such personal data in accordance with the data processing agreement attached to the Agreement.
          </p>
          <p>
            <span className="font-semibold">7.2.</span> The Client represents and warrants that it has the legal right to disclose any personal data that is made available to Eniris under or in connection with this SLA and the Client has a valid legal ground to process such personal data and disclose it to Eniris.
          </p>
          <p>
            <span className="font-semibold">7.3.</span> This SLA shall automatically renew for successive periods of one (1) year unless either party provides written notice of termination at least two (2) months prior to the end of the then-current term.
          </p>

          {/* Section 8: Limitation of Liability */}
          <h2 className="text-2xl font-semibold mt-8">8. Limitation of Liability</h2>
          <p>
            <span className="font-semibold">8.1.</span> The limitation of liability as set out in the Agreement shall apply to this SLA.
          </p>
          
          {/* Section 9: Term and Termination */}
          <h2 className="text-2xl font-semibold mt-8">9. Term and Termination</h2>
          <p>
            <span className="font-semibold">9.1.</span> This SLA shall commence on the Effective Date and shall continue in effect for the duration of the Agreement.
          </p>
          <p>
            <span className="font-semibold">9.2.</span> Upon termination or expiration of the Agreement this SLA shall automatically terminate.
          </p>
          <p>
            <span className="font-semibold">9.3.</span> This SLA may be suspended and shall be deemed void if The Client fails to adhere to all agreed terms, including but not limited to payment obligations.
          </p>

        </div>
      </div>
    </div>
  );
};

export default ServiceLevelAgreement;
